import * as yup from "yup";
import { useTranslation } from "react-i18next";
import useBigNumber from "src/shared/services/bigNumber/useBigNumber";

export const appl302ValidationSchema = () => {
  const { t } = useTranslation();
  const { toBigNumber, multiply, plus, isZero, equals } = useBigNumber();

  return yup
    .object()
    .shape({
      coYearsToClose: yup
        .string()
        .nullable()
        .max(
          4,
          t(
            "Το πεδίο Διάρκεια εταιρείας μπορεί να περιλαμβάνει μέχρι 4 χαρακτήρες.",
          ),
        )
        .test(
          "is-positive-integer",
          t("Το πεδίο πρέπει να είναι θετικός ακέραιος και όχι 0."),
          (value) => {
            if (value === null || value === undefined || value === "") {
              return true;
            } else {
              return /^[1-9]\d*?$/.test(value.toString());
            }
          },
        )
        .when("coAoristi", {
          is: true,
          then: (schema) => schema.notRequired().nullable(),
          otherwise: (schema) => schema.nullable(),
        }),
      d50_id: yup.object().required(t("Το πεδίο είναι υποχρεωτικό")),
      docPraktDSDate: yup
        .date()
        .typeError(t("Το πεδίο πρέπει να είναι ημερομηνία"))
        .required(t("Το πεδίο είναι υποχρεωτικό")),
      docStatuteDate: yup
        .date()
        .typeError(t("Το πεδίο πρέπει να είναι ημερομηνία"))
        .required(t("Το πεδίο είναι υποχρεωτικό")),
      coUsageYearStart: yup
        .date()
        .typeError(t("Το πεδίο πρέπει να είναι ημερομηνία"))
        .nullable(),
      coUsageYearEnd: yup
        .date()
        .typeError(t("Το πεδίο πρέπει να είναι ημερομηνία"))
        .nullable(),
      coEndDate: yup
        .date()
        .typeError(t("Το πεδίο πρέπει να είναι ημερομηνία"))
        .nullable(),
      dsStatuteArticle: yup
        .string()
        .nullable()
        .max(
          4,
          t(
            "Το πεδίο Άρθρο καταστατικού μπορεί να περιλαμβάνει μέχρι 4 χαρακτήρες.",
          ),
        )
        .test(
          "is-positive-integer",
          t("Το πεδίο πρέπει να είναι θετικός ακέραιος και όχι 0."),
          (value) => {
            if (value === null || value === undefined || value === "") {
              return true;
            } else {
              return /^[1-9]\d*?$/.test(value.toString());
            }
          },
        ),
      dsService: yup
        .string()
        .nullable()
        .max(
          4,
          t(
            "Το πεδίο Διάρκεια Θητείας μπορεί να περιλαμβάνει μέχρι 4 χαρακτήρες.",
          ),
        )
        .test(
          "is-positive-integer",
          t("Το πεδίο πρέπει να είναι θετικός ακέραιος και όχι 0."),
          (value) => {
            if (value === null || value === undefined || value === "") {
              return true;
            } else {
              return /^[1-9]\d*?$/.test(value.toString());
            }
          },
        ),
      fudocPraktDS: yup.string().required(t("Το πεδίο είναι υποχρεωτικό")),
      fudocStatute: yup.string().required(t("Το πεδίο είναι υποχρεωτικό")),
      changedArticles: yup.array().of(
        yup.object().shape({
          statuteArticleNum: yup
            .string()
            .required(t("Το πεδίο είναι υποχρεωτικό"))
            .test(
              "is-integer",
              t(
                "Ο αριθμός άρθρου που τροποποιήθηκε πρέπει να είναι αριθμός και το πεδίο δεν μπορεί να είναι κενό (π.χ. 12 και όχι 12α).",
              ),
              (value) =>
                value === undefined ||
                value === null ||
                /^\d+?$/.test(value.toString()),
            ),
          statuteArticleDesc: yup
            .string()
            .required(t("Το πεδίο είναι υποχρεωτικό")),
        }),
      ),
      capital: yup
        .string()
        .transform((_, val) => (val !== "" ? val : null))
        .nullable()
        .test(
          "two-decimal-places",
          `${t("Το πεδίο πρέπει να είναι αριθμός")}. ${t("Το ποσό πρέπει να έχει μέχρι δύο δεκαδικά ψηφία με τελεία (πχ. 9999.99)")}.`,
          (value) =>
            value === undefined ||
            value === null ||
            /^\d+(\.\d{1,2})?$/.test(value.toString()),
        ),
      oldShareCap1: yup
        .string()
        .transform((_, val) => (val !== "" ? val : null))
        .nullable()
        .test(
          "is-integer",
          t("Μόνο ακέραιοι αριθμοί επιτρέπονται"),
          (value) =>
            value === undefined ||
            value === null ||
            /^\d+?$/.test(value.toString()),
        ),
      shareCap1: yup
        .string()
        .transform((_, val) => (val !== "" ? val : null))
        .nullable()
        .test(
          "is-integer",
          t("Μόνο ακέραιοι αριθμοί επιτρέπονται"),
          (value) =>
            value === undefined ||
            value === null ||
            /^\d+?$/.test(value.toString()),
        ),
      oldShareCap3: yup
        .string()
        .transform((_, val) => (val !== "" ? val : null))
        .nullable()
        .test(
          "is-integer",
          t("Μόνο ακέραιοι αριθμοί επιτρέπονται"),
          (value) =>
            value === undefined ||
            value === null ||
            /^\d+?$/.test(value.toString()),
        ),
      shareCap3: yup
        .string()
        .transform((_, val) => (val !== "" ? val : null))
        .nullable()
        .test(
          "is-integer",
          t("Μόνο ακέραιοι αριθμοί επιτρέπονται"),
          (value) =>
            value === undefined ||
            value === null ||
            /^\d+?$/.test(value.toString()),
        ),
      oldShareCap5: yup
        .string()
        .transform((_, val) => (val !== "" ? val : null))
        .nullable()
        .test(
          "is-integer",
          t("Μόνο ακέραιοι αριθμοί επιτρέπονται"),
          (value) =>
            value === undefined ||
            value === null ||
            /^\d+?$/.test(value.toString()),
        ),
      shareCap5: yup
        .string()
        .transform((_, val) => (val !== "" ? val : null))
        .nullable()
        .test(
          "is-integer",
          t("Μόνο ακέραιοι αριθμοί επιτρέπονται"),
          (value) =>
            value === undefined ||
            value === null ||
            /^\d+?$/.test(value.toString()),
        ),
      oldShareCapVal1: yup
        .string()
        .transform((_, val) => (val !== "" ? val : null))
        .nullable()
        .test(
          "two-decimal-places",
          `${t("Το πεδίο πρέπει να είναι αριθμός")}. ${t("Το ποσό πρέπει να έχει μέχρι δύο δεκαδικά ψηφία με τελεία (πχ. 9999.99)")}.`,
          (value) =>
            value === undefined ||
            value === null ||
            /^\d+(\.\d{1,2})?$/.test(value.toString()),
        ),
      shareCapVal1: yup
        .string()
        .transform((_, val) => (val !== "" ? val : null))
        .nullable()
        .test(
          "two-decimal-places",
          `${t("Το πεδίο πρέπει να είναι αριθμός")}. ${t("Το ποσό πρέπει να έχει μέχρι δύο δεκαδικά ψηφία με τελεία (πχ. 9999.99)")}.`,
          (value) =>
            value === undefined ||
            value === null ||
            /^\d+(\.\d{1,2})?$/.test(value.toString()),
        ),
      oldShareCapVal3: yup
        .string()
        .transform((_, val) => (val !== "" ? val : null))
        .nullable()
        .test(
          "two-decimal-places",
          `${t("Το πεδίο πρέπει να είναι αριθμός")}. ${t("Το ποσό πρέπει να έχει μέχρι δύο δεκαδικά ψηφία με τελεία (πχ. 9999.99)")}.`,
          (value) =>
            value === undefined ||
            value === null ||
            /^\d+(\.\d{1,2})?$/.test(value.toString()),
        ),
      shareCapVal3: yup
        .string()
        .transform((_, val) => (val !== "" ? val : null))
        .nullable()
        .test(
          "two-decimal-places",
          `${t("Το πεδίο πρέπει να είναι αριθμός")}. ${t("Το ποσό πρέπει να έχει μέχρι δύο δεκαδικά ψηφία με τελεία (πχ. 9999.99)")}.`,
          (value) =>
            value === undefined ||
            value === null ||
            /^\d+(\.\d{1,2})?$/.test(value.toString()),
        ),
      oldShareCapVal5: yup
        .string()
        .transform((_, val) => (val !== "" ? val : null))
        .nullable()
        .test(
          "two-decimal-places",
          `${t("Το πεδίο πρέπει να είναι αριθμός")}. ${t("Το ποσό πρέπει να έχει μέχρι δύο δεκαδικά ψηφία με τελεία (πχ. 9999.99)")}.`,
          (value) =>
            value === undefined ||
            value === null ||
            /^\d+(\.\d{1,2})?$/.test(value.toString()),
        ),
      shareCapVal5: yup
        .string()
        .transform((_, val) => (val !== "" ? val : null))
        .nullable()
        .test(
          "two-decimal-places",
          `${t("Το πεδίο πρέπει να είναι αριθμός")}. ${t("Το ποσό πρέπει να έχει μέχρι δύο δεκαδικά ψηφία με τελεία (πχ. 9999.99)")}.`,
          (value) =>
            value === undefined ||
            value === null ||
            /^\d+(\.\d{1,2})?$/.test(value.toString()),
        ),
    })
    .test("capital-match", function (value) {
      const {
        capital,
        oldShareCap1,
        shareCap1,
        oldShareCapVal1,
        shareCapVal1,
        oldShareCap3,
        shareCap3,
        oldShareCapVal3,
        shareCapVal3,
        oldShareCap5,
        shareCap5,
        oldShareCapVal5,
        shareCapVal5,
      } = value;

      const fields = [capital, shareCap1, shareCap3, shareCap5];

      for (const field of fields) {
        if (field && !/^\d+(\.\d{1,2})?$/.test(field?.toString())) {
          return;
        }
      }

      let cost = toBigNumber("0");
      let shareCount = toBigNumber("0");
      let shareValue = toBigNumber("0");
      let checkShares: boolean = false;
      let changeInShares: boolean = false;
      if (shareCap1 || shareCapVal1) {
        checkShares = checkShares || !(shareCap1 && shareCapVal1);
      }
      if (shareCap3 || shareCapVal3) {
        checkShares = checkShares || !(shareCap3 && shareCapVal3);
      }
      if (shareCap5 || shareCapVal5) {
        checkShares = checkShares || !(shareCap5 && shareCapVal5);
      }
      if (checkShares) {
        return this.createError({
          message: t(
            "Υπάρχει πρόβλημα με τη συμπλήρωση των Στοιχείων Κεφαλαίου. Όταν συμπληρώνεται το πεδίο Νέο Πλήθος Μετοχών για κάποιον τύπο μετοχής πρέπει να συμπληρώνεται και το αντίστοιχο πεδίο Νέα Ονομαστική Αξία Μετοχής και το αντίστροφο.",
          ),
        });
      }
      if (shareCap1) {
        changeInShares = true;
        shareCount = toBigNumber(shareCap1 || "0");
        shareValue = toBigNumber(shareCapVal1 || "0");
        cost = plus(cost, multiply(shareCount, shareValue));
      }
      if (shareCap3) {
        changeInShares = true;
        shareCount = toBigNumber(shareCap3 || "0");
        shareValue = toBigNumber(shareCapVal3 || "0");
        cost = plus(cost, multiply(shareCount, shareValue));
      }
      if (shareCap5) {
        changeInShares = true;
        shareCount = toBigNumber(shareCap5 || "0");
        shareValue = toBigNumber(shareCapVal5 || "0");
        cost = plus(cost, multiply(shareCount, shareValue));
      }
      if (oldShareCap1 && !shareCap1) {
        shareCount = toBigNumber(oldShareCap1 || "0");
        shareValue = toBigNumber(oldShareCapVal1 || "0");
        cost = plus(cost, multiply(shareCount, shareValue));
      }
      if (oldShareCap3 && !shareCap3) {
        shareCount = toBigNumber(oldShareCap3 || "0");
        shareValue = toBigNumber(oldShareCapVal3 || "0");
        cost = plus(cost, multiply(shareCount, shareValue));
      }
      if (oldShareCap5 && !shareCap5) {
        shareCount = toBigNumber(oldShareCap5 || "0");
        const shareValue = toBigNumber(oldShareCapVal5 || "0");
        cost = plus(cost, multiply(shareCount, shareValue));
      }
      if (changeInShares && !isZero(cost) && !capital) {
        return this.createError({
          message: t(
            "Έχετε συμπληρώσει τύπο μετοχής, αλλά δεν έχετε συμπληρώσει το τρέχον κεφάλαιο.",
          ),
        });
      }
      const capitalBN = toBigNumber(capital || "0");
      if (capital && !equals(cost, capitalBN)) {
        return this.createError({
          message: t(
            "Υπάρχει αναντιστοιχία των μετοχών με το τρέχον κεφάλαιο.",
          ),
        });
      }
      const minCapital = toBigNumber("25000");
      if (capital && capitalBN.lessThan(minCapital)) {
        return this.createError({
          message: t("Το κεφάλαιο πρέπει να είναι τουλάχιστον 25000.00 ευρώ."),
        });
      }
      return true;
    })
    .test(
      "at-least-one-article",
      t("Πρέπει να εισάγετε αναλυτικά τις τροποποιήσεις του καταστατικού."),
      (value) => value.changedArticles && value.changedArticles.length > 0,
    );
};
